import { useState, useEffect } from "react";
import { Globe, Plus, Trash2, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { listKnowledge, addManualKnowledge, deleteKnowledge, crawlSite } from "@/lib/api";

export default function KnowledgeTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [baseUrl, setBaseUrl] = useState("https://www.drhuseyintirman.com");
  const [maxPages, setMaxPages] = useState(40);
  const [manual, setManual] = useState({ title: "", content: "" });

  const load = async () => {
    setLoading(true);
    try {
      setItems(await listKnowledge());
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onCrawl = async () => {
    setCrawling(true);
    try {
      const r = await crawlSite({ base_url: baseUrl, max_pages: maxPages });
      toast.success(`${r.crawled} sayfa eklendi`);
      load();
    } catch (e) {
      toast.error("Tarama başarısız");
    } finally { setCrawling(false); }
  };

  const onAddManual = async () => {
    if (!manual.title || !manual.content) return;
    try {
      await addManualKnowledge({ ...manual, source: "manual" });
      toast.success("Eklendi");
      setManual({ title: "", content: "" });
      load();
    } catch { toast.error("Hata"); }
  };

  const onDelete = async (id) => {
    await deleteKnowledge(id);
    toast.success("Silindi");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <Card data-testid="crawl-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display"><Globe className="w-4 h-4" /> Web Sitesini Tara</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input data-testid="crawl-url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://..." />
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="quick-drht" onClick={() => setBaseUrl("https://www.drhuseyintirman.com")} className="text-[11px] px-2.5 py-1 rounded-full border border-border/60 hover:bg-secondary/60 transition-colors">drhuseyintirman.com</button>
              <button type="button" data-testid="quick-3dtech" onClick={() => setBaseUrl("https://www.3dtechcare.com")} className="text-[11px] px-2.5 py-1 rounded-full border border-border/60 hover:bg-secondary/60 transition-colors">3dtechcare.com</button>
            </div>
            <div className="flex items-center gap-2">
              <Input data-testid="crawl-maxpages" type="number" value={maxPages} onChange={(e) => setMaxPages(parseInt(e.target.value || 40))} className="w-32" />
              <Button data-testid="crawl-btn" onClick={onCrawl} disabled={crawling} className="flex-1" style={{ background: "#B39B7A" }}>
                {crawling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Taranıyor...</> : <><Globe className="w-4 h-4 mr-2" /> Taramayı Başlat</>}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">İki resmi siteyi de buraya ekleyebilirsiniz: AI önce <b>drhuseyintirman.com</b>, sonra <b>3dtechcare.com</b> bilgilerini birlikte kullanır. İkisinde de yoksa müşteriyi iletişim formuna yönlendirir.</p>
          </CardContent>
        </Card>

        <Card data-testid="manual-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display"><FileText className="w-4 h-4" /> Manuel İçerik Ekle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input data-testid="manual-title" placeholder="Başlık" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} />
            <Textarea data-testid="manual-content" placeholder="İçerik..." rows={5} value={manual.content} onChange={(e) => setManual({ ...manual, content: e.target.value })} />
            <Button data-testid="manual-add" onClick={onAddManual} className="w-full" style={{ background: "#8B9B88" }}>
              <Plus className="w-4 h-4 mr-2" /> Ekle
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-display">Bilgi Tabanı ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-muted-foreground py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">Henüz içerik yok. Tarayın veya manuel ekleyin.</div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} data-testid={`kb-item-${it.id}`} className="flex items-center justify-between p-3 rounded-xl border border-border/40 hover:border-primary/50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">{it.source}</Badge>
                      <div className="font-medium text-sm truncate">{it.title}</div>
                    </div>
                    {it.url && <div className="text-xs text-muted-foreground truncate mt-1">{it.url}</div>}
                  </div>
                  <Button data-testid={`kb-delete-${it.id}`} variant="ghost" size="icon" onClick={() => onDelete(it.id)} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
